import React, { useEffect, useState, useCallback } from "react";
import api, { API, getErrorMessage } from "@/lib/api";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { QrCode, Download, Check, X, AlertTriangle, Search, ZoomIn, Loader2 } from "lucide-react";

const STATUSES = [["", "All"], ["pending", "Pending"], ["matched", "Matched"], ["approved", "Approved"], ["rejected", "Rejected"]];

export default function AdminQrPayments() {
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const [zoom, setZoom] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/qr-payments", { params: { status, q, date } });
    setD(data);
  }, [status, q, date]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <Loader />;

  const approve = async (id) => {
    setBusy(id);
    try { await api.post(`/admin/qr-payments/${id}/approve`); toast.success("Approved — subscription activated ✅"); load(); }
    catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusy(null); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reason (amount wrong / UTR not found / duplicate / other):", "");
    if (reason === null) return;
    setBusy(id);
    try { await api.post(`/admin/qr-payments/${id}/reject`, { reason }); toast.success("Rejected"); load(); }
    catch { toast.error("Failed"); }
    finally { setBusy(null); }
  };
  const viewShot = async (id) => {
    try {
      const res = await api.get(`/qr/screenshot/${id}`, { responseType: "blob" });
      setZoom(URL.createObjectURL(res.data));
    } catch { toast.error("Could not load screenshot."); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold flex items-center gap-2"><QrCode className="w-7 h-7 text-thali-green" /> QR Payments</h1>
        <a href={`${API}/admin/qr-payments/export`} className="btn-ghost text-sm" data-testid="qr-export"><Download className="w-4 h-4" /> Export Excel</a>
      </div>

      <div className="rounded-xl bg-thali-turmeric/20 border border-thali-turmeric/40 p-3 text-sm flex items-start gap-2" data-testid="qr-admin-reminder">
        <AlertTriangle className="w-5 h-5 text-thali-saffron shrink-0" /> Check the UTR and amount in your Razorpay Dashboard (Payments / QR Codes) before approving. Fake screenshots are common.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Pending", d.counts.pending, "#EA580C"], ["Matched ✓", d.counts.matched, "#0891B2"], ["Approved", d.counts.approved, "#0B6B3A"], ["Rejected", d.counts.rejected, "#DB2777"]].map(([l, v, c], i) => (
          <div key={i} className="card-thali p-4" data-testid={`qr-count-${i}`}><div className="text-xs text-thali-ink/60">{l}</div><div className="font-num font-extrabold text-2xl" style={{ color: c }}>{v}</div></div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-thali w-auto text-sm" data-testid="qr-filter-status">
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-thali w-auto text-sm" data-testid="qr-filter-date" />
        <div className="relative flex-1 min-w-[160px]">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-thali-ink/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, mobile or UTR" className="input-thali pl-8 text-sm" data-testid="qr-search" />
        </div>
      </div>

      {d.requests.length === 0 ? <div className="card-thali text-sm text-thali-ink/50">No QR payments.</div> : (
        <div className="space-y-3">
          {d.requests.map((r) => (
            <div key={r.id} className="card-thali" data-testid={`qr-req-${r.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="min-w-0">
                  <div className="font-bold">{r.name} <span className="text-thali-ink/50 font-normal text-sm">{r.mobile}</span></div>
                  <div className="text-sm text-thali-ink/70">UTR: <b className="font-num">{r.utr}</b> · ₹{r.amount} · {r.upi_app}</div>
                  <div className="text-xs text-thali-ink/50">Paid: {(r.paid_at || "").replace("T", " ")} · Req {r.request_code} · Submitted {(r.created_at || "").slice(0, 10)}</div>
                  {r.reason && <div className="text-xs text-red-600 mt-1">Rejected: {r.reason}</div>}
                </div>
                <div className="flex items-center gap-2">
                  {r.matched && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold" data-testid={`qr-confirmed-${r.id}`}>✓ Confirmed by Razorpay</span>}
                  <span className={`text-xs px-2 py-1 rounded-full font-bold ${r.status === "approved" ? "bg-green-100 text-green-700" : r.status === "rejected" ? "bg-red-100 text-red-600" : r.status === "matched" ? "bg-cyan-100 text-cyan-700" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
                </div>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap items-center">
                <button onClick={() => viewShot(r.id)} className="btn-ghost text-xs" data-testid={`qr-shot-${r.id}`}><ZoomIn className="w-4 h-4" /> Screenshot</button>
                {r.status !== "approved" && r.status !== "rejected" && (
                  <>
                    <button onClick={() => approve(r.id)} disabled={busy === r.id} className="btn-3d-green text-xs" data-testid={`qr-approve-${r.id}`}>{busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {r.matched ? "✓ Confirmed — Approve" : "Approve & activate"}</button>
                    <button onClick={() => reject(r.id)} disabled={busy === r.id} className="btn-ghost text-xs text-red-600" data-testid={`qr-reject-${r.id}`}><X className="w-4 h-4" /> Reject</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {zoom && (
        <div className="fixed inset-0 z-[95] bg-black/80 grid place-items-center p-4" onClick={() => { URL.revokeObjectURL(zoom); setZoom(null); }} data-testid="qr-shot-zoom">
          <button className="absolute top-4 right-4 text-white"><X className="w-6 h-6" /></button>
          <img src={zoom} alt="payment screenshot" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}
    </div>
  );
}
